/**
 * provider.tsx — StateProvider component for @hostesa/state.
 *
 * Wraps react-redux <Provider> with a lazily-created store instance.
 * Accepts an optional `store` prop so tests can inject a custom store.
 *
 * Usage (app root):
 *   <StateProvider>
 *     <App />
 *   </StateProvider>
 *
 * Usage (tests):
 *   const testStore = createAppStore({ ui: { sidebarCollapsed: true, ... } });
 *   <StateProvider store={testStore}>
 *     <ComponentUnderTest />
 *   </StateProvider>
 */

import type { ReactNode } from 'react';
import { useRef } from 'react';
import { Provider } from 'react-redux';
import type { AppStore } from './store';
import { createAppStore } from './store';

export interface StateProviderProps {
  children: ReactNode;
  /** Optional pre-created store. Useful for tests and server-side rendering. */
  store?: AppStore;
}

export function StateProvider({ children, store }: StateProviderProps) {
  const storeRef = useRef<AppStore>(null);
  if (storeRef.current === null) {
    storeRef.current = store ?? createAppStore();
  }
  return <Provider store={storeRef.current}>{children}</Provider>;
}
